/**
 * Top-3 podium for the gamification leaderboard.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';
import { LeaderboardRankBadge } from '@/components/gamification/LeaderboardRankBadge';
import {
  PODIUM_BADGE_SIZE,
  PODIUM_CARD_CLASS,
  PODIUM_EMPTY_SLOT_CLASS,
  PODIUM_LIST_CLASS,
  PODIUM_ORDER,
  PODIUM_RING_CLASS,
  PODIUM_SIZE_CLASS,
} from '@/components/gamification/gamification-configs';
import {
  podiumColumnClass,
  visiblePodiumRanks,
} from '@/components/gamification/gamification-utils';
import type { PodiumRank } from '@/types/gamification.types';

/** Props for {@link LeaderboardPodium}. */
export interface LeaderboardPodiumProps {
  /** Map rank (1–3) to rendered card content */
  slots: Partial<Record<PodiumRank, React.ReactNode>>;
  className?: string;
}

/** Top-3 podium; empty ranks collapse the grid to 1 or 2 columns. */
export const LeaderboardPodium: React.FC<LeaderboardPodiumProps> = ({ slots, className }) => {
  function renderPodiumSlot(rank: PodiumRank): React.ReactNode {
    const content = slots[rank];
    if (content == null) {
      return <li key={rank} className={PODIUM_EMPTY_SLOT_CLASS} aria-hidden />;
    }
    return (
      <li
        key={rank}
        className={cn(PODIUM_CARD_CLASS, PODIUM_SIZE_CLASS[rank], PODIUM_RING_CLASS[rank])}
      >
        <LeaderboardRankBadge
          rank={rank}
          size={PODIUM_BADGE_SIZE[rank]}
          className="mb-2 shrink-0"
        />
        {content}
      </li>
    );
  }

  const filled = visiblePodiumRanks(slots);
  if (filled.length === 0) return null;

  return (
    <ul
      className={cn(PODIUM_LIST_CLASS, podiumColumnClass(filled.length), className)}
      aria-label="Podium — top 3"
    >
      {PODIUM_ORDER.map(renderPodiumSlot)}
    </ul>
  );
};
